import { isValidTimeValue } from './isValidTimeValue'
import unformatDate from './unformatDate'
import { Dispatch, SetStateAction, ReactElement } from 'react'

// Define the allowed date formats
const DEFAULT_BACKEND_DATE_FORMAT = 'dd-MM-yyyy'
const DEFAULT_BACKEND_DATE_FORMAT_REVERSE = 'yyyy-MM-dd'

type DateFormat =
  | typeof DEFAULT_BACKEND_DATE_FORMAT
  | typeof DEFAULT_BACKEND_DATE_FORMAT_REVERSE
type SortDirection = 'asc' | 'desc'
type SortableItem = Record<string, any>

/**
 * Sorts two items alphabetically by the given property
 */
export const sortString = (
  a: SortableItem,
  b: SortableItem,
  sortBy: string
): number => {
  return a[sortBy] !== undefined && b[sortBy] !== undefined
    ? a[sortBy].toString().localeCompare(b[sortBy].toString())
    : a[sortBy] !== undefined
    ? -1
    : b[sortBy] !== undefined
    ? 1
    : 0
}

const collator = new Intl.Collator('en', { numeric: true, sensitivity: 'base' })

/**
 * Sorts two items alpha-numerically by the given property
 */
export const sortAlphaNum = (
  a: SortableItem,
  b: SortableItem,
  sortBy: string
): number => {
  return a[sortBy] !== undefined && b[sortBy] !== undefined
    ? collator.compare(a[sortBy], b[sortBy])
    : a[sortBy] !== undefined
    ? -1
    : b[sortBy] !== undefined
    ? -1
    : 0
}

/**
 * Sorts two items numerically by the given property
 */
export const sortNumber = (
  a: SortableItem,
  b: SortableItem,
  sortBy: string
): number => {
  return a[sortBy] !== undefined && b[sortBy] !== undefined
    ? a[sortBy] - b[sortBy]
    : a[sortBy] !== undefined
    ? -1
    : b[sortBy] !== undefined
    ? 1
    : 0
}

/**
 * Sorts two items by date property
 */
export const sortDate = (
  a: SortableItem,
  b: SortableItem,
  sortBy: string,
  format?: DateFormat
): number => {
  let aValue = a[sortBy]
  let bValue = b[sortBy]
  if (format) {
    aValue = unformatDate(a[sortBy], format)
    bValue = unformatDate(b[sortBy], format)
  }

  return isValidTimeValue(aValue) && isValidTimeValue(bValue)
    ? bValue - aValue
    : 0
}

interface InvoiceDateElement extends ReactElement {
  props: {
    children: Array<{
      props: {
        children: string
      }
    }>
  }
}

/**
 * Sorts two items by invoice date property
 */
export const sortInvoiceDate = (
  a: SortableItem,
  b: SortableItem,
  sortBy: string
): number => {
  if (a[sortBy] !== undefined && b[sortBy] !== undefined) {
    if (sortBy.toLowerCase().includes('due_date')) {
      const [dayFirst, monthFirst, yearFirst] = (
        b[sortBy] as InvoiceDateElement
      ).props.children[0].props.children.split('-')
      const [dayLast, monthLast, yearLast] = (
        a[sortBy] as InvoiceDateElement
      ).props.children[0].props.children.split('-')
      const dateFirst = new Date(
        +yearFirst,
        parseInt(monthFirst) - 1,
        +dayFirst
      )
      const dateLast = new Date(+yearLast, parseInt(monthLast) - 1, +dayLast)
      return dateLast.getTime() - dateFirst.getTime()
    } else {
      const [dayFirst, monthFirst, yearFirst] = (b[sortBy] as string).split('-')
      const [dayLast, monthLast, yearLast] = (a[sortBy] as string).split('-')
      const dateFirst = new Date(
        +yearFirst,
        parseInt(monthFirst) - 1,
        +dayFirst
      )
      const dateLast = new Date(+yearLast, parseInt(monthLast) - 1, +dayLast)
      return dateLast.getTime() - dateFirst.getTime()
    }
  } else {
    return a[sortBy] !== undefined ? -1 : b[sortBy] !== undefined ? 1 : 0
  }
}

/**
 * Sorts items based on the sortBy field and updates state
 */
export const setItemsSort = (
  sortBy: string,
  setSortBy: Dispatch<SetStateAction<string>>,
  sort: SortDirection,
  setSort: Dispatch<SetStateAction<SortDirection>>,
  setItems: Dispatch<SetStateAction<SortableItem[]>>
): void => {
  setSortBy(sortBy)
  switch (sort) {
    case 'asc':
      setSort('desc')
      setItems(prev =>
        prev.sort((a, b) => {
          if (sortBy.toLowerCase().includes('date')) {
            if (a[sortBy] !== undefined && b[sortBy] !== undefined) {
              const [dayFirst, monthFirst, yearFirst] = (
                a[sortBy] as string
              ).split('-')
              const [dayLast, monthLast, yearLast] = (
                b[sortBy] as string
              ).split('-')
              const dateFirst = new Date(
                +yearFirst,
                parseInt(monthFirst) - 1,
                +dayFirst
              )
              const dateLast = new Date(
                +yearLast,
                parseInt(monthLast) - 1,
                +dayLast
              )
              return dateLast.getTime() - dateFirst.getTime()
            } else {
              return a[sortBy] !== undefined
                ? -1
                : b[sortBy] !== undefined
                ? 1
                : 0
            }
          } else if (typeof a[sortBy] === 'number') {
            return a[sortBy] !== undefined && b[sortBy] !== undefined
              ? a[sortBy] - b[sortBy]
              : a[sortBy] !== undefined
              ? -1
              : b[sortBy] !== undefined
              ? 1
              : 0
          } else if (
            sortBy.toLowerCase().includes('supplier') ||
            sortBy.toLowerCase().includes('operation') ||
            sortBy.toLowerCase().includes('tenant') ||
            sortBy.toLowerCase().includes('landlord') ||
            sortBy.toLowerCase().includes('account') ||
            sortBy.toLowerCase().includes('list_task') ||
            sortBy.toLowerCase().includes('assigned_to')
          ) {
            return a[sortBy]?.display !== undefined &&
              b[sortBy]?.display !== undefined
              ? a[sortBy].display
                  .toString()
                  .localeCompare(b[sortBy].display.toString())
              : a[sortBy]?.display !== undefined
              ? -1
              : b[sortBy]?.display !== undefined
              ? 1
              : 0
          } else if (
            sortBy.toLowerCase().includes('floors') ||
            sortBy.toLowerCase().includes('fee_rules')
          ) {
            return a[sortBy]?.[0]?.display !== undefined &&
              b[sortBy]?.[0]?.display !== undefined
              ? a[sortBy][0].display
                  .toString()
                  .localeCompare(a[sortBy][0].display.toString())
              : a[sortBy]?.[0]?.display !== undefined
              ? -1
              : b[sortBy]?.[0]?.display !== undefined
              ? 1
              : 0
          } else if (sortBy.toLowerCase().includes('investor')) {
            return a[sortBy]?.name !== undefined &&
              b[sortBy]?.name !== undefined
              ? a[sortBy].name
                  .toString()
                  .localeCompare(b[sortBy].name.toString())
              : a[sortBy]?.name !== undefined
              ? -1
              : b[sortBy]?.name !== undefined
              ? 1
              : 0
          } else {
            return a[sortBy] !== undefined && b[sortBy] !== undefined
              ? a[sortBy].toString().localeCompare(b[sortBy].toString())
              : a[sortBy] !== undefined
              ? -1
              : b[sortBy] !== undefined
              ? 1
              : 0
          }
        })
      )
      break

    case 'desc':
      setSort('asc')
      setItems(prev =>
        prev.sort((a, b) => {
          if (sortBy?.toLowerCase()?.includes('date')) {
            if (a[sortBy] !== undefined && b[sortBy] !== undefined) {
              const [dayFirst, monthFirst, yearFirst] = (
                a[sortBy] as string
              ).split('-')
              const [dayLast, monthLast, yearLast] = (
                b[sortBy] as string
              ).split('-')
              const dateFirst = new Date(
                +yearFirst,
                parseInt(monthFirst) - 1,
                +dayFirst
              )
              const dateLast = new Date(
                +yearLast,
                parseInt(monthLast) - 1,
                +dayLast
              )
              return dateFirst.getTime() - dateLast.getTime()
            } else {
              return a[sortBy] !== undefined
                ? 1
                : b[sortBy] !== undefined
                ? -1
                : 0
            }
          } else if (typeof a[sortBy] === 'number') {
            return a[sortBy] !== undefined && b[sortBy] !== undefined
              ? b[sortBy] - a[sortBy]
              : a[sortBy] !== undefined
              ? 1
              : b[sortBy] !== undefined
              ? -1
              : 0
          } else if (
            sortBy.toLowerCase().includes('supplier') ||
            sortBy.toLowerCase().includes('operation') ||
            sortBy.toLowerCase().includes('tenant') ||
            sortBy.toLowerCase().includes('list_task') ||
            sortBy.toLowerCase().includes('landlord') ||
            sortBy.toLowerCase().includes('account') ||
            sortBy.toLowerCase().includes('assigned_to')
          ) {
            return a[sortBy]?.display !== undefined &&
              b[sortBy]?.display !== undefined
              ? b[sortBy].display
                  .toString()
                  .localeCompare(a[sortBy].display.toString())
              : a[sortBy]?.display !== undefined
              ? 1
              : b[sortBy]?.display !== undefined
              ? -1
              : 0
          } else if (
            sortBy.toLowerCase().includes('floors') ||
            sortBy.toLowerCase().includes('fee_rules')
          ) {
            return a[sortBy]?.[0]?.display !== undefined &&
              b[sortBy]?.[0]?.display !== undefined
              ? b[sortBy][0].display
                  .toString()
                  .localeCompare(a[sortBy][0].display.toString())
              : a[sortBy]?.[0]?.display !== undefined
              ? 1
              : b[sortBy]?.[0]?.display !== undefined
              ? -1
              : 0
          } else if (sortBy.toLowerCase().includes('investor')) {
            return a[sortBy]?.name !== undefined &&
              b[sortBy]?.name !== undefined
              ? b[sortBy].name
                  .toString()
                  .localeCompare(a[sortBy].name.toString())
              : a[sortBy]?.name !== undefined
              ? 1
              : b[sortBy]?.name !== undefined
              ? -1
              : 0
          } else {
            return a[sortBy] !== undefined && b[sortBy] !== undefined
              ? b[sortBy].toString().localeCompare(a[sortBy].toString())
              : a[sortBy] !== undefined
              ? 1
              : b[sortBy] !== undefined
              ? -1
              : 0
          }
        })
      )
      break
  }
}

interface ApiSortingParams {
  field: string
  direction?: SortDirection
}

/**
 * Prepares a sort parameter for API requests
 */
export const apiSorting = ({
  field,
  direction
}: ApiSortingParams): { sort_by: string } => {
  const fieldToAdd = direction === 'desc' ? '-' + field : field
  return { sort_by: direction ? fieldToAdd : '' }
}
